<script setup lang="ts">
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant,
} from '../buttons.constants';
import {
  baseClass,
  colorClass,
  iconSizeForIconButton,
  sizeClass,
} from '../buttons.styles';
import { IconButtonShape } from './icon-button.constants';
import Icon from '../../icon/icon.vue';
import { IconName } from '../../icon/icon.constants';
import { useLink } from '../../../composables/use-link';

type Props = {
  /** Which glyph to render inside the button. */
  iconName: IconName;
  /**
   * Accessible name for the button. Required — an icon-only button has no
   * visible label, so this is rendered as `aria-label`.
   */
  label: string;
  /** Size of the button (square — height equals width). */
  size?: ButtonSize;
  /** Visual style variant (filled, outlined, or text). */
  variant?: ButtonVariant;
  /** Semantic color applied according to the chosen `variant`. */
  color?: ButtonColor;
  /** Corner shape — a full circle (default) or a rounded square. */
  shape?: IconButtonShape;
  /** Native button `type` attribute (when rendered as a `<button>`). */
  type?: ButtonType;
  /**
   * Router target. When set, renders as the host app's link component
   * (NuxtLink or RouterLink). Takes precedence over `href`.
   */
  to?: RouteLocationRaw;
  /** External/plain URL. When set (and `to` is not), renders as an `<a>`. */
  href?: string;
  /** Id of the associated `<form>`, forwarded to the native `form` attribute. */
  form?: string;
};

const props = withDefaults(defineProps<Props>(), {
  size: ButtonSize.MD,
  variant: ButtonVariant.FILLED,
  color: ButtonColor.PRIMARY,
  shape: IconButtonShape.CIRCLE,
});

// Host app supplies the link component (NuxtLink in app, RouterLink in admin).
const linkComponent = useLink();

const iconSize = computed(() => iconSizeForIconButton[props.size]);

const shapeClass: Record<IconButtonShape, string> = {
  [IconButtonShape.ROUNDED]: 'rounded-[8px]',
  [IconButtonShape.CIRCLE]: 'rounded-full',
};

const iconButtonClass = computed(() => [
  ...baseClass,
  sizeClass[props.size],
  shapeClass[props.shape],
  colorClass[props.variant][props.color],
  // Square: width tracks the fixed height, no horizontal padding.
  'aspect-square px-none',
]);

const tag = computed(() => {
  if (props.to !== undefined) {
    return linkComponent;
  }

  if (props.href !== undefined) {
    return 'a';
  }

  return 'button';
});

const parsedType = computed<ButtonType | null>(() => {
  if (props.type) {
    return props.type;
  }
  if (props.to === undefined && props.href === undefined) {
    return ButtonType.BUTTON;
  }

  return null;
});
</script>

<template>
  <component
    :is="tag"
    :to="to"
    :type="parsedType"
    :href="href"
    :form="props.form"
    :aria-label="props.label"
    :class="iconButtonClass"
  >
    <Icon :name="props.iconName" :size="iconSize" />
  </component>
</template>
